import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { fmtAmount, chartPalette } from "@/lib/util";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DownloadSimple, ChartPieSlice, Buildings, Storefront, Tag, CalendarBlank, Users } from "@phosphor-icons/react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line } from "recharts";
import CurrencyIcon from "@/components/CurrencyIcon";

const BASIS_OPTIONS = [
  { value: "paid",       label: "Paid date"      },  // when money actually left
  { value: "created",    label: "Requested date" },  // when the invoice was raised
  { value: "accounting", label: "Accounting date"},  // when it was booked
];

export default function Reports() {
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  // Default range: first of the current month → today, so users always see
  // meaningful numbers on landing and always know exactly what window is on screen.
  const monthStart = (() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  })();
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(todayStr);
  const [basis, setBasis] = useState("paid");  // default: reports track money-out

  // Serialize `date_to` as end-of-day so a same-day range (e.g. Aug 6 → Aug 6)
  // actually includes today's records. Otherwise "2026-08-06" < "2026-08-06T14:00…"
  // lexicographically and we'd silently miss everything from that day.
  const dateToInclusive = dateTo ? `${dateTo}T23:59:59` : "";

  useEffect(() => { api.get("/brands").then((r) => setBrands(r.data.filter((b) => b.is_active))); }, []);
  useEffect(() => {
    const params = { basis };
    if (brandId) params.brand_id = brandId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateToInclusive) params.date_to = dateToInclusive;
    api.get("/reports/summary", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [brandId, dateFrom, dateToInclusive, basis]);

  const download = async (currency, format = "xlsx") => {
    const params = new URLSearchParams();
    if (brandId) params.set("brand_id", brandId);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateToInclusive) params.set("date_to", dateToInclusive);
    if (currency) params.set("currency", currency);
    const token = localStorage.getItem("access_token") || "";
    const resp = await fetch(`${API}/reports/export.${format}?${params}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payments${currency ? "_" + currency : ""}.${format}`; a.click();
    URL.revokeObjectURL(url);
  };

  const overviewPie = rows.map((r, i) => ({ name: r.currency, value: r.total, fill: chartPalette[i % chartPalette.length] }));
  // Children receive dateTo as the plain string (they'll widen when calling API).
  const filters = { brandId, dateFrom, dateTo, basis };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1700px]">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="overline">Reports</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">
            <span className="bg-gradient-to-br from-amber-500 to-slate-500 bg-clip-text text-transparent">Payment intelligence</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="reports-export-csv" onClick={() => download("", "csv")} className="silver-pill inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold">
            <DownloadSimple size={16} weight="bold" /> Export CSV
          </button>
          <button data-testid="reports-export-all" onClick={() => download("", "xlsx")} className="silver-pill inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold">
            <DownloadSimple size={16} weight="bold" /> Export Excel
          </button>
        </div>
      </header>

      <div className="glass rounded-3xl p-5 flex flex-wrap items-end gap-3 mb-6">
        <div className="w-56">
          <Label className="text-xs font-semibold tracking-wide">DATE BASIS</Label>
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger data-testid="reports-basis" className="rounded-xl bg-white/70 border-white/60 h-10 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BASIS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-64">
          <Label className="text-xs font-semibold tracking-wide">BRAND</Label>
          <Select value={brandId || "all"} onValueChange={(v) => setBrandId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="reports-brand" className="rounded-xl bg-white/70 border-white/60 h-10 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brands</SelectItem>
              {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs font-semibold tracking-wide">FROM</Label><Input data-testid="reports-from" type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="rounded-xl bg-white/70 border-white/60 h-10 mt-1" /></div>
        <div><Label className="text-xs font-semibold tracking-wide">TO</Label><Input data-testid="reports-to" type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="rounded-xl bg-white/70 border-white/60 h-10 mt-1" /></div>
        <div className="flex items-end gap-2">
          <button
            data-testid="reports-this-month"
            onClick={() => { setDateFrom(monthStart); setDateTo(todayStr); }}
            className="h-10 px-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 text-xs font-bold hover:bg-amber-100 whitespace-nowrap"
          >This month</button>
          <button
            data-testid="reports-this-week"
            onClick={() => {
              const d = new Date(); const dow = d.getDay(); const diff = dow === 0 ? 6 : dow - 1;
              d.setDate(d.getDate() - diff);
              setDateFrom(d.toISOString().slice(0, 10)); setDateTo(todayStr);
            }}
            className="h-10 px-3 rounded-xl border border-slate-300 bg-white/70 text-slate-700 text-xs font-semibold hover:bg-white whitespace-nowrap"
          >This week</button>
          <button
            data-testid="reports-clear-dates"
            onClick={() => { setDateFrom(""); setDateTo(""); }}
            className="h-10 px-3 rounded-xl border border-slate-300 bg-white/70 text-slate-600 text-xs font-semibold hover:bg-white whitespace-nowrap"
          >All time</button>
        </div>
      </div>

      {/* Active-range banner so the user always knows what window is on screen */}
      <div data-testid="reports-range-banner" className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/60 border border-amber-200 text-slate-700">
        <CalendarBlank size={12} weight="fill" className="text-amber-700" />
        Showing <span className="font-mono text-amber-900">
          {dateFrom || "the beginning"} → {dateTo || "today"}
        </span>
        · <span className="text-slate-500 normal-case">basis: {BASIS_OPTIONS.find((o) => o.value === basis)?.label || basis}</span>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="rounded-xl flex-wrap h-auto glass p-1 gap-1">
          <TabsTrigger value="overview" data-testid="rpt-tab-overview" className="tab-pill rounded-lg px-4 py-2 font-semibold text-xs inline-flex items-center gap-1.5"><ChartPieSlice size={13} weight="fill" /> Overview</TabsTrigger>
          <TabsTrigger value="brand"    data-testid="rpt-tab-brand"    className="tab-pill rounded-lg px-4 py-2 font-semibold text-xs inline-flex items-center gap-1.5"><Buildings size={13} weight="fill" /> By brand</TabsTrigger>
          <TabsTrigger value="vendor"   data-testid="rpt-tab-vendor"   className="tab-pill rounded-lg px-4 py-2 font-semibold text-xs inline-flex items-center gap-1.5"><Storefront size={13} weight="fill" /> By vendor</TabsTrigger>
          <TabsTrigger value="expense"  data-testid="rpt-tab-expense"  className="tab-pill rounded-lg px-4 py-2 font-semibold text-xs inline-flex items-center gap-1.5"><Tag size={13} weight="fill" /> By category</TabsTrigger>
          <TabsTrigger value="month"    data-testid="rpt-tab-month"    className="tab-pill rounded-lg px-4 py-2 font-semibold text-xs inline-flex items-center gap-1.5"><CalendarBlank size={13} weight="fill" /> Trend</TabsTrigger>
          <TabsTrigger value="people"   data-testid="rpt-tab-people"   className="tab-pill rounded-lg px-4 py-2 font-semibold text-xs inline-flex items-center gap-1.5"><Users size={13} weight="fill" /> By person</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewSection rows={rows} overviewPie={overviewPie} download={download} />
        </TabsContent>
        <TabsContent value="brand" className="mt-6"><GroupSection endpoint="/reports/by-brand" filters={filters} title="Payments by brand" testid="rpt-brand" groupType="brand" /></TabsContent>
        <TabsContent value="vendor" className="mt-6"><GroupSection endpoint="/reports/by-vendor" filters={filters} title="Payments by vendor" testid="rpt-vendor" groupType="vendor" /></TabsContent>
        <TabsContent value="expense" className="mt-6"><GroupSection endpoint="/reports/by-expense-type" filters={filters} title="Payments by expense category" testid="rpt-expense" groupType="expense" /></TabsContent>
        <TabsContent value="month" className="mt-6"><TrendSection filters={filters} /></TabsContent>
        <TabsContent value="people" className="mt-6"><PeopleSection filters={filters} /></TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewSection({ rows, overviewPie, download }) {
  if (rows.length === 0) return <EmptyState />;
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 glass rounded-3xl p-4 sm:p-6 shimmer-border">
          <div className="overline mb-1">Overview</div>
          <div className="font-heading font-bold text-xl mb-4">Paid &amp; verified — by currency</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={rows.map((r) => ({ currency: r.currency, paid: r.stages?.paid?.total || 0, verified: r.stages?.verified?.total || 0 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(180,180,190,0.3)" />
                <XAxis dataKey="currency" stroke="#7c6b76" fontSize={11} />
                <YAxis stroke="#7c6b76" fontSize={11} />
                <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: "1px solid #D4A01744", borderRadius: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="paid" fill="#F5C842" radius={[6, 6, 0, 0]} />
                <Bar dataKey="verified" fill="#B8B8C5" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
          <div className="overline mb-1">Distribution</div>
          <div className="font-heading font-bold text-xl mb-2">Total by currency</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={overviewPie} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={3}>
                  {overviewPie.map((e, i) => <Cell key={i} fill={e.fill} stroke="rgba(255,255,255,0.9)" strokeWidth={2} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: "1px solid #D4A01744", borderRadius: 12 }} formatter={(v, n) => [fmtAmount(v, n), ""]} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <Tabs defaultValue={rows[0].currency} className="w-full">
        <TabsList className="rounded-xl flex-wrap h-auto glass p-1 gap-1">
          {rows.map((r) => (
            <TabsTrigger key={r.currency} value={r.currency} data-testid={`reports-tab-${r.currency}`} className="tab-pill rounded-lg px-4 py-2 font-mono text-xs font-semibold inline-flex items-center gap-2">
              <CurrencyIcon code={r.currency} size={16} /> {r.currency}
            </TabsTrigger>
          ))}
        </TabsList>
        {rows.map((r) => (
          <TabsContent key={r.currency} value={r.currency} className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
              <StatBox label="Total paid+verified" value={fmtAmount(r.total)} highlight />
              {["pending_audit", "approved", "paid", "verified", "rejected"].map((s) => {
                const v = r.stages?.[s];
                return <StatBox key={s} label={s.replace("_", " ")} value={fmtAmount(v?.total || 0)} sub={`${v?.count || 0} req`} />;
              })}
            </div>
            <button data-testid={`export-${r.currency}`} onClick={() => download(r.currency)} className="silver-pill inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold">
              <DownloadSimple size={14} weight="bold" /> Export {r.currency} to Excel
            </button>
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
}

function GroupSection({ endpoint, filters, title, testid, groupType }) {
  const [data, setData] = useState([]);
  const dateToInclusive = filters.dateTo ? `${filters.dateTo}T23:59:59` : "";
  useEffect(() => {
    const p = { basis: filters.basis };
    if (filters.brandId) p.brand_id = filters.brandId;
    if (filters.dateFrom) p.date_from = filters.dateFrom;
    if (dateToInclusive) p.date_to = dateToInclusive;
    api.get(endpoint, { params: p }).then((r) => setData(r.data)).catch(() => setData([]));
  }, [endpoint, filters.brandId, filters.dateFrom, dateToInclusive, filters.basis]);

  if (data.length === 0) return <EmptyState />;
  // Determine which currencies appear
  const currencies = Array.from(new Set(data.flatMap((r) => Object.keys(r.totals || {})))).sort();
  // Compute grand totals per currency across all rows
  const grandSpent = {};
  const grandTotals = {};
  let grandCount = 0;
  for (const row of data) {
    grandCount += row.count || 0;
    for (const c of currencies) {
      grandSpent[c] = (grandSpent[c] || 0) + (row.spent?.[c] || 0);
      grandTotals[c] = (grandTotals[c] || 0) + (row.totals?.[c] || 0);
    }
  }
  // Build the Queue drill-down URL for a given row. `groupType` tells us which
  // filter to plug in: brand_id, vendor name, or expense_type. Also carries
  // the date-range and current basis, plus stage_in=paid,verified so the drill
  // matches exactly what the Report totals aggregated.
  const drillUrl = (row) => {
    const p = new URLSearchParams();
    if (groupType === "brand")  p.set("brand", row.key);
    if (groupType === "vendor") p.set("vendor", row.label);
    // Note: Queue has no expense-type filter today — expense drill falls back
    // to date+basis scope so the user at least lands in the right time-window.
    if (filters.dateFrom) p.set("date_from", filters.dateFrom);
    if (filters.dateTo)   p.set("date_to",   filters.dateTo);
    // Match the exact aggregation: paid+verified when basis=paid/accounting
    if (filters.basis === "paid" || filters.basis === "accounting") {
      p.set("stage_in", "paid,verified");
      p.set("basis", filters.basis);
    }
    return `/queue?${p.toString()}`;
  };

  return (
    <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
      <div className="overline mb-1">Grouped · {BASIS_OPTIONS.find((o) => o.value === filters.basis)?.label || filters.basis}</div>
      <div className="font-heading font-bold text-xl mb-4">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full ledger-table text-sm" data-testid={`${testid}-table`}>
          <thead>
            <tr className="text-left">
              <th className="px-3 py-2 overline">Name</th>
              <th className="px-3 py-2 overline text-right">Requests</th>
              {currencies.map((c) => (
                <th key={c} className="px-3 py-2 overline text-right">
                  <div className="inline-flex items-center gap-1"><CurrencyIcon code={c} size={13} /> {c} paid</div>
                </th>
              ))}
              {currencies.map((c) => (
                <th key={"tot-" + c} className="px-3 py-2 overline text-right text-slate-400">
                  <div className="inline-flex items-center gap-1">{c} total</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.key} data-testid={`${testid}-row-${row.label}`} className="hover:bg-amber-50/40 transition-colors">
                <td className="px-3 py-3 font-medium">
                  <Link to={drillUrl(row)} data-testid={`${testid}-drill-${row.label}`} className="text-amber-800 hover:underline hover:text-amber-900">
                    {row.label}
                  </Link>
                </td>
                <td className="px-3 py-3 font-mono text-right">{row.count}</td>
                {currencies.map((c) => (
                  <td key={c} className="px-3 py-3 font-mono text-right font-semibold text-emerald-800">
                    {row.spent?.[c] ? fmtAmount(row.spent[c]) : <span className="text-slate-300">—</span>}
                  </td>
                ))}
                {currencies.map((c) => (
                  <td key={"tot-" + c} className="px-3 py-3 font-mono text-right text-slate-500 text-xs">
                    {row.totals?.[c] ? fmtAmount(row.totals[c]) : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr data-testid={`${testid}-totals`} className="bg-amber-100/50 border-t-2 border-amber-300 font-bold">
              <td className="px-3 py-3 uppercase tracking-wider text-xs text-amber-900">Total</td>
              <td className="px-3 py-3 font-mono text-right text-amber-900">{grandCount}</td>
              {currencies.map((c) => (
                <td key={"g-" + c} className="px-3 py-3 font-mono text-right text-emerald-900">
                  {grandSpent[c] ? fmtAmount(grandSpent[c]) : "—"}
                </td>
              ))}
              {currencies.map((c) => (
                <td key={"gt-" + c} className="px-3 py-3 font-mono text-right text-slate-700">
                  {grandTotals[c] ? fmtAmount(grandTotals[c]) : "—"}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-slate-500">
        <b className="text-emerald-800">Paid</b> = actually paid or verified within range. <b className="text-slate-500">Total</b> = includes pending / approved / rejected. Click any row to drill into the payments.
      </p>
    </div>
  );
}

function TrendSection({ filters }) {
  const [data, setData] = useState([]);
  const dateToInclusive = filters.dateTo ? `${filters.dateTo}T23:59:59` : "";
  useEffect(() => {
    const p = { basis: filters.basis };
    if (filters.brandId) p.brand_id = filters.brandId;
    if (filters.dateFrom) p.date_from = filters.dateFrom;
    if (dateToInclusive) p.date_to = dateToInclusive;
    api.get("/reports/by-month", { params: p }).then((r) => setData(r.data)).catch(() => setData([]));
  }, [filters.brandId, filters.dateFrom, dateToInclusive, filters.basis]);

  if (data.length === 0) return <EmptyState />;
  const currencies = Array.from(new Set(data.flatMap((r) => Object.keys(r.spent || {})))).sort();
  // Sort chronologically by billing_month
  const sorted = [...data].sort((a, b) => (a.label > b.label ? 1 : -1));
  const chart = sorted.map((r) => ({ month: r.label, ...(r.spent || {}) }));

  return (
    <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
      <div className="overline mb-1">Spend trend</div>
      <div className="font-heading font-bold text-xl mb-4">Payments per billing month</div>
      <div className="h-72">
        <ResponsiveContainer>
          <LineChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(180,180,190,0.3)" />
            <XAxis dataKey="month" stroke="#7c6b76" fontSize={11} />
            <YAxis stroke="#7c6b76" fontSize={11} />
            <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: "1px solid #D4A01744", borderRadius: 12 }} formatter={(v, n) => [fmtAmount(v, n), n]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {currencies.map((c, i) => (
              <Line key={c} type="monotone" dataKey={c} stroke={chartPalette[i % chartPalette.length]} strokeWidth={2.5} dot={{ r: 3 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function PeopleSection({ filters }) {
  const [data, setData] = useState(null);
  const dateToInclusive = filters.dateTo ? `${filters.dateTo}T23:59:59` : "";
  useEffect(() => {
    const p = {};
    if (filters.dateFrom) p.date_from = filters.dateFrom;
    if (dateToInclusive) p.date_to = dateToInclusive;
    api.get("/reports/by-user", { params: p }).then((r) => setData(r.data)).catch(() => setData({ requesters: [], auditors: [], payers: [] }));
  }, [filters.dateFrom, dateToInclusive]);

  if (!data) return <div className="glass rounded-3xl p-10 text-center text-sm text-slate-500">Loading…</div>;
  const total = (data.requesters?.length || 0) + (data.auditors?.length || 0) + (data.payers?.length || 0);
  if (total === 0) return <EmptyState />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <PersonList title="Requesters" tint="slate" rows={data.requesters} />
      <PersonList title="Auditors" tint="sky" rows={data.auditors} />
      <PersonList title="Payers" tint="emerald" rows={data.payers} />
    </div>
  );
}

function PersonList({ title, rows, tint }) {
  const color = { slate: "text-slate-800", sky: "text-sky-800", emerald: "text-emerald-800" }[tint];
  return (
    <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
      <div className="overline mb-3">{title}</div>
      {(!rows || rows.length === 0) && <div className="text-sm text-slate-500">None in range</div>}
      <ul className="space-y-2">
        {(rows || []).map((r) => (
          <li key={r.user_id} className="rounded-xl bg-white/60 border border-amber-100 p-3">
            <div className="flex items-center justify-between">
              <span className={`font-semibold ${color}`}>{r.name}</span>
              <span className="font-mono text-xs text-slate-600">{r.count} req</span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {Object.entries(r.by_currency || {}).map(([c, v]) => (
                <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/80 border border-slate-200 text-[11px] font-mono">
                  <CurrencyIcon code={c} size={11} /> {fmtAmount(v)} {c}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="glass rounded-3xl p-10 text-center text-sm text-slate-500">
      <ChartPieSlice size={28} className="mx-auto text-slate-400 mb-2" />
      No data for selected filters.
    </div>
  );
}

function StatBox({ label, value, sub, highlight }) {
  return (
    <div className={`glass rounded-2xl p-4 ${highlight ? "shimmer-border" : ""}`}>
      <div className="overline">{label}</div>
      <div className={`font-heading font-black text-2xl mt-2 font-mono ${highlight ? "bg-gradient-to-br from-amber-500 to-slate-600 bg-clip-text text-transparent" : ""}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
